import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, AlertTriangle, MousePointerClick, ExternalLink, Info } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { usePowerBiUsage } from '../hooks/usePowerBiUsage';
import { PowerBiUsageStatsBar } from '../components/powerbi-usage/PowerBiUsageStats';
import { PowerBiUsageFiltersBar } from '../components/powerbi-usage/PowerBiUsageFilters';
import { PowerBiUsageTable } from '../components/powerbi-usage/PowerBiUsageTable';
import { PowerBiUsageDetailDrawer } from '../components/powerbi-usage/PowerBiUsageDetailDrawer';
import { PowerBiReviewModal } from '../components/powerbi-usage/PowerBiReviewModal';
import { PBI_REVIEW_ORDER, PBI_REVIEW_META, toReviewStatus } from '../components/powerbi-usage/status';
import { ContextMenu } from '../components/ui/ContextMenu';
import { useToast } from '../components/ui/Toast';
import { powerBiUsageApi } from '../services/powerBiUsageApi';
import { powerBiReportUrl } from '../types/powerBiUsage';
import type { PbiReportRow, PbiReviewStatus } from '../types/powerBiUsage';
import { fmtDateTime, fmtDate } from '../components/powerbi-usage/format';

const DRAWER_WIDTH_KEY = 'powerbi-usage-drawer-width';
const DEFAULT_DRAWER_WIDTH = 55;

interface ReviewTarget {
    reportId: string;
    name: string;
    currentStatus: PbiReviewStatus;
    currentNote: string | null;
}

export function PowerBiUsagePage() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/powerbi-usage');
    const { toast } = useToast();
    const {
        items, total, loading, error, stats, filterOptions,
        filters, window: usageWindow, sortBy, sortOrder, page, limit,
        setFilters, setWindow, setSort, setPage, refresh,
    } = usePowerBiUsage();

    const [selectedRow, setSelectedRow] = useState<PbiReportRow | null>(null);
    const [writeBusy, setWriteBusy] = useState(false);
    const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
    const [statusMenu, setStatusMenu] = useState<{ x: number; y: number; target: ReviewTarget } | null>(null);
    const [reviewModal, setReviewModal] = useState<{ target: ReviewTarget; next: PbiReviewStatus } | null>(null);
    const [rowMenu, setRowMenu] = useState<{ x: number; y: number; row: PbiReportRow } | null>(null);

    const [drawerWidth, setDrawerWidth] = useState<number>(() => {
        if (typeof window === 'undefined') return DEFAULT_DRAWER_WIDTH;
        const stored = window.localStorage.getItem(DRAWER_WIDTH_KEY);
        const parsed = stored ? Number(stored) : NaN;
        return Number.isFinite(parsed) && parsed >= 40 && parsed <= 85 ? parsed : DEFAULT_DRAWER_WIDTH;
    });
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(DRAWER_WIDTH_KEY, String(Math.round(drawerWidth)));
        }
    }, [drawerWidth]);

    const afterWrite = useCallback(() => {
        refresh();
        setDrawerRefreshKey(k => k + 1);
    }, [refresh]);

    const applyReview = useCallback(async (target: ReviewTarget, status: PbiReviewStatus, statusNote: string | null) => {
        setWriteBusy(true);
        try {
            await powerBiUsageApi.setReview(target.reportId, { status, statusNote });
            toast(`${target.name} — ${PBI_REVIEW_META[status].label}`, 'success');
            setReviewModal(null);
            afterWrite();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to set review status', 'error');
        } finally {
            setWriteBusy(false);
        }
    }, [toast, afterWrite]);

    // Keep/Retire ask for a note (the decision trail); Undecided resets outright.
    const pickStatus = useCallback((target: ReviewTarget, next: PbiReviewStatus) => {
        if (next === 'Undecided') void applyReview(target, next, null);
        else setReviewModal({ target, next });
    }, [applyReview]);

    const rowToTarget = (row: PbiReportRow): ReviewTarget => ({
        reportId: row.reportId,
        name: row.name,
        currentStatus: toReviewStatus(row.reviewStatus),
        currentNote: row.reviewNote,
    });

    if (permLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
        );
    }

    const totalPages = Math.ceil(total / limit);
    const drawerOpen = selectedRow !== null;

    // Collector health: no run yet = zero-state; last event >72h old = stale banner
    // (events lag up to 24h by design; 72h means the nightly collector missed runs).
    const neverRan = stats !== null && stats.lastSuccessfulRunAt === null;
    const lastEventAt = stats?.lastEventAt ?? null;
    const isStale = (() => {
        if (!lastEventAt || neverRan) return false;
        const t = new Date(lastEventAt).getTime();
        return Number.isFinite(t) && (Date.now() - t) > 72 * 3600 * 1000;
    })();

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Power BI Usage</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Who uses which report, how often — and which reports can be retired.
                    </p>
                </div>
                {stats?.lastEventAt && (
                    <span className="text-xs text-gray-500">
                        Data through {fmtDateTime(stats.lastEventAt)} (UTC)
                        {stats.collectionSince && ` · collecting since ${fmtDate(stats.collectionSince)}`}
                    </span>
                )}
            </div>

            {neverRan && (
                <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    <Info className="h-4 w-4 shrink-0" />
                    The usage collector has not run yet — numbers appear after the first import
                    (Admin → import preview/run, then the nightly timer takes over).
                </div>
            )}

            {isStale && (
                <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Usage data is more than 72 hours old — check the nightly collector on func-tippspiel-reporting.
                </div>
            )}

            {!canView && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    You do not have access to this page.
                </div>
            )}

            {/* KPI tiles */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                <PowerBiUsageStatsBar stats={stats} filters={filters} onChange={setFilters} />
            </div>

            {/* Filters */}
            <PowerBiUsageFiltersBar
                filters={filters}
                options={filterOptions}
                window={usageWindow}
                onChange={setFilters}
                onWindowChange={setWindow}
            />

            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Table + Drawer split */}
            <div className="flex min-h-0 gap-3">
                <div className="relative min-w-0" style={{ width: drawerOpen ? `${100 - drawerWidth}%` : '100%' }}>
                    {loading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    )}
                    <PowerBiUsageTable
                        items={items}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={setSort}
                        onSelect={setSelectedRow}
                        selectedId={selectedRow?.reportId ?? null}
                        compact={drawerOpen}
                        canEdit={canEdit}
                        onStatusClick={(row, e) => setStatusMenu({ x: e.clientX, y: e.clientY, target: rowToTarget(row) })}
                        onRowContextMenu={(row, e) => setRowMenu({ x: e.clientX, y: e.clientY, row })}
                    />
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                        <MousePointerClick className="h-3.5 w-3.5" />
                        Left-click a row for details · click the review badge to change it · right-click for actions ·
                        the link icon opens the report in Power BI.
                    </p>
                </div>
                {drawerOpen && selectedRow && (
                    <PowerBiUsageDetailDrawer
                        reportId={selectedRow.reportId}
                        window={usageWindow}
                        onClose={() => setSelectedRow(null)}
                        width={drawerWidth}
                        onWidthChange={setDrawerWidth}
                        canEdit={canEdit}
                        refreshKey={drawerRefreshKey}
                        onPickStatus={(detail, status) => pickStatus({
                            reportId: detail.reportId,
                            name: detail.name,
                            currentStatus: toReviewStatus(detail.review.status),
                            currentNote: detail.review.note,
                        }, status)}
                    />
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <p className="text-sm text-gray-600">
                        Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total.toLocaleString('de-CH')}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(page - 1)}
                            disabled={page <= 1}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronLeft className="h-4 w-4" /> Prev
                        </button>
                        <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                        <button
                            onClick={() => setPage(page + 1)}
                            disabled={page >= totalPages}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Review-status menu (from the table's review badge) */}
            {statusMenu && (
                <ContextMenu
                    x={statusMenu.x}
                    y={statusMenu.y}
                    onClose={() => setStatusMenu(null)}
                    items={PBI_REVIEW_ORDER.map(s => {
                        const meta = PBI_REVIEW_META[s];
                        const Icon = meta.icon;
                        const current = statusMenu.target.currentStatus === s;
                        return {
                            label: current ? `${meta.label} (current)` : meta.label,
                            icon: <Icon className={`h-3.5 w-3.5 ${meta.iconClass}`} />,
                            disabled: current,
                            onClick: () => pickStatus(statusMenu.target, s),
                        };
                    })}
                />
            )}

            {/* Row context menu */}
            {rowMenu && (
                <ContextMenu
                    x={rowMenu.x}
                    y={rowMenu.y}
                    onClose={() => setRowMenu(null)}
                    items={[
                        ...(canEdit ? PBI_REVIEW_ORDER.map(s => {
                            const meta = PBI_REVIEW_META[s];
                            const Icon = meta.icon;
                            const current = toReviewStatus(rowMenu.row.reviewStatus) === s;
                            return {
                                label: current ? `${meta.label} (current)` : `Set ${meta.label}`,
                                icon: <Icon className={`h-3.5 w-3.5 ${meta.iconClass}`} />,
                                disabled: current,
                                onClick: () => pickStatus(rowToTarget(rowMenu.row), s),
                            };
                        }) : []),
                        ...(powerBiReportUrl(rowMenu.row) ? [{
                            label: 'Open in Power BI',
                            icon: <ExternalLink className="h-3.5 w-3.5" />,
                            onClick: () => { window.open(powerBiReportUrl(rowMenu.row) as string, '_blank', 'noopener,noreferrer'); },
                        }] : []),
                    ]}
                />
            )}

            {reviewModal && (
                <PowerBiReviewModal
                    target={reviewModal.next}
                    reportName={reviewModal.target.name}
                    initialNote={reviewModal.target.currentNote}
                    busy={writeBusy}
                    onConfirm={({ status, statusNote }) => void applyReview(reviewModal.target, status, statusNote)}
                    onCancel={() => setReviewModal(null)}
                />
            )}
        </div>
    );
}
